/**
 * Framework-agnostic Drupal Batch API polling loop.
 *
 * Drives the `?op=do&_format=json` long-poll cycle Drupal's
 * Batch API serves for interactive batch operations. Every
 * caller that used to roll their own polling loop (the SPA
 * navigation batch modal, the React useConfirmModal hook) now
 * goes through this helper so the protocol is implemented
 * exactly once and bug fixes land in every consumer at the
 * same time.
 *
 * Usage: construct with a batch URL and a set of callbacks,
 * then call `run()`. The driver calls `onProgress` with each
 * percentage + message pair, `onFinished` when the batch
 * completes, and `onError` on network or parse failure. No
 * caller ever touches fetch() directly - which is the point.
 *
 * Only the polling protocol lives here. The modal UI, the
 * completion continuation (SPA navigate vs full reload), and
 * any visual flourishes stay with the caller - this class is
 * deliberately UI-free so both browser widgets and framework
 * hooks can layer their own presentation on top.
 */

const DEFAULT_POLL_INTERVAL_MS = 200;

export interface BatchProgress {
  percentage: number;
  message: string;
  label: string;
}

export interface BatchPollerHandlers {
  onProgress?: (progress: BatchProgress) => void;
  onFinished: () => void;
  onError?: (error: unknown) => void;
}

export interface BatchPollerOptions extends BatchPollerHandlers {
  /** The batch URL the form redirect landed on, e.g. `/batch?op=start&id=17`. */
  batchUrl: string;
  /** Optional override for the base path used when building poll URLs. */
  basePath?: string;
  /** Poll interval in milliseconds (default: 200). */
  pollIntervalMs?: number;
}

interface BatchPollResponse {
  status?: number;
  percentage?: string | number;
  message?: string;
  label?: string;
}

/**
 * Drives a Drupal Batch API polling cycle to completion.
 *
 * The loop terminates on any of four conditions:
 *
 * - The server returns `status: 0` (batch finished).
 * - The server reports percentage >= 100 (batch finished).
 * - `stop()` is called by the caller.
 * - A network or JSON parse error occurs (onError + finish).
 */
export class BatchPoller {
  private readonly batchUrl: string;
  private readonly basePath: string;
  private readonly pollInterval: number;
  private readonly handlers: BatchPollerHandlers;
  private stopped = false;

  constructor(options: BatchPollerOptions) {
    this.batchUrl = options.batchUrl;
    this.basePath = options.basePath ?? BatchPoller.defaultBasePath();
    this.pollInterval = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
    this.handlers = {
      onProgress: options.onProgress,
      onFinished: options.onFinished,
      onError: options.onError,
    };
  }

  /**
   * Starts the polling loop. Safe to call once per poller; a
   * second call is a no-op while the loop is still running.
   */
  run(): void {
    const batchId = this.extractBatchId(this.batchUrl);
    if (batchId === null) {
      this.handlers.onFinished();
      return;
    }
    const baseSegment = this.basePath.replace(/\/$/, '');
    const batchUri = `${baseSegment}/batch?id=${encodeURIComponent(batchId)}`;
    this.poll(batchUri);
  }

  /**
   * Stops the polling loop. After stop() the poller is inert;
   * neither onProgress nor onFinished fire on any pending
   * response that arrives later.
   */
  stop(): void {
    this.stopped = true;
  }

  private poll(batchUri: string): void {
    if (this.stopped) return;
    fetch(`${batchUri}&op=do&_format=json`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: '',
    })
      .then((response) => response.json() as Promise<BatchPollResponse>)
      .then((data) => {
        if (this.stopped) return;
        if (!data || data.status === 0) {
          this.handlers.onFinished();
          return;
        }
        const percentage = this.parsePercentage(data.percentage);
        this.handlers.onProgress?.({
          percentage,
          message: typeof data.message === 'string' ? data.message : '',
          label: typeof data.label === 'string' ? data.label : '',
        });
        if (percentage >= 100) {
          fetch(`${batchUri}&op=finished`, { credentials: 'same-origin' })
            .finally(() => {
              if (!this.stopped) this.handlers.onFinished();
            });
          return;
        }
        // Sequential polling paces the loop - the setTimeout is
        // deliberate UX pacing rather than an async wait hack.
        setTimeout(() => this.poll(batchUri), this.pollInterval);
      })
      .catch((err: unknown) => {
        if (this.stopped) return;
        this.handlers.onError?.(err);
        this.handlers.onFinished();
      });
  }

  private extractBatchId(batchUrl: string): string | null {
    try {
      return new URL(batchUrl, location.origin).searchParams.get('id');
    }
    catch {
      return null;
    }
  }

  private parsePercentage(raw: unknown): number {
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string') {
      const parsed = parseInt(raw, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }

  private static defaultBasePath(): string {
    if (typeof drupalSettings === 'undefined') {
      return '/';
    }
    const path = drupalSettings.path as { baseUrl?: string } | undefined;
    return path?.baseUrl ?? '/';
  }
}
